/* eslint-disable @typescript-eslint/naming-convention */
import { type App, app, user } from '@ps/web-core-analytics';
import { Cookie as CookieNames, OddsFormat } from '@ps/web-core-store';
import { OptimizelyXPlugin } from '@snowplow/browser-plugin-optimizely-x';
import { TimezonePlugin } from '@snowplow/browser-plugin-timezone';
import { newTracker, setUserId } from '@snowplow/browser-tracker';
import { isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { useSelector, useStore } from 'react-redux';
import Cookies from 'universal-cookie';

import { FeatureFlag, GADimension } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useFirstRender from '@swf/core/hooks/useFirstRender';
import useOneTrust from '@swf/core/hooks/useOneTrust';
import { select } from '@swf/core/store';

import useTrackers from '../hooks/useTrackers';

export const AnalyticsContext = React.createContext<{ initialized: boolean }>({
  initialized: false
});

const cookies = new Cookies();
const analyticsCookies = [
  '_ga',
  '_gid',
  '_gat',
  '_gat_gtag_xxxxxxxxxxxxxxxxxxxxxxxxxxx',
  '_gat_xxxxxxxxxxxxxxxxxxxxxxxxxx',
  '_sp_ses.xxxx',
  '_sp_id.xxxx',
  'sp'
];

const isAnalayticsAllowed = () => {
  const activeGroups = window?.OnetrustActiveGroups?.split(',') || [];
  const domainGroups = window?.OneTrust?.GetDomainData().Groups || [];
  const notAllowedCookies = domainGroups.reduce<string[]>((memo, group) => {
    if (activeGroups.includes(group.OptanonGroupId)) {
      return memo;
    }
    return [...memo, ...group.Cookies.map((cookie) => cookie.Name)];
  }, []);

  return !notAllowedCookies.find((cookie) => analyticsCookies.includes(cookie));
};

const oddsFormatMap: Record<OddsFormat, App['oddsFormat']> = {
  [OddsFormat.Decimal]: 'DECIMAL',
  [OddsFormat.Fractional]: 'FRACTIONAL',
  [OddsFormat.American]: 'AMERICAN'
};

const AnalyticsProvider: React.FC = ({ children }) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [analyticsConsented, setAnalyticsConsented] = useState<boolean>(false);
  const firstRender = useFirstRender();
  const store = useStore();
  const { alertClosed, oneTrust, oneTrustEnabled } = useOneTrust();
  const hasClientSession = useSelector(select.session.hasClientSession);
  const userWebId = useSelector(select.session.getWebId);
  const brandCode = useSelector(select.cms.getBrandCode);
  const brandName = useSelector(select.cms.getBrandName);
  const { trackingId } = useSelector(select.config.get);
  const currency = useSelector(select.currency.get);
  const cookieDomain = useSelector(select.location.getCookieDomain);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const locale = useSelector(select.locale.get);
  const { pageView } = useTrackers();
  const analyticsPath = useSelector(select.location.getAnalyticsPath);
  const pageTitle = useSelector(select.cms.getPageTitle);
  const prevAnalyticsPath = useRef(analyticsPath);
  const firstPageView = useRef(true);
  const playMode = useSelector(select.currency.getPlayMode);
  const siteName = useSelector(select.cms.getSiteName);
  const snowplowEnabled = useFeature(FeatureFlag.SnowplowAnalytics);
  const { acid, ba_hostapptype, ba_hostappversion, datasource, msclkid, visitid } = useSelector(
    select.location.getQuery
  );
  const enableAnalyticsUS = useSelector(select.cms.isSiteUS);

  useEffect(() => {
    if (!analyticsConsented) {
      setAnalyticsConsented(
        !oneTrustEnabled || (alertClosed && isAnalayticsAllowed()) || enableAnalyticsUS
      );
      oneTrust?.OnConsentChanged(() => {
        setAnalyticsConsented(!oneTrustEnabled || isAnalayticsAllowed());
      });
    }
    if (!initialized && analyticsConsented && trackingId) {
      ReactGA.initialize(trackingId, { debug: !!localStorage.getItem('GA_DEBUG') });
      setInitialized(true);
    }
  }, [
    alertClosed,
    analyticsConsented,
    initialized,
    oneTrust,
    oneTrustEnabled,
    trackingId,
    enableAnalyticsUS
  ]);

  useEffect(() => {
    if (initialized) {
      ReactGA.set({
        [GADimension.License]: siteName,
        [GADimension.LangId]: locale,
        [GADimension.WebClientID]: trackingId,
        [GADimension.ACID]: acid,
        [GADimension.WebIDHit]: cookies.get(CookieNames.WEB_ID),
        [GADimension.TimeStamp]: new Date().getTime().toString(),
        [GADimension.SizmekID]: cookies.get(CookieNames.SIZMEK_UID),
        [GADimension.MaxymiserID]: cookies.get(CookieNames.MAXYMISER_ID),
        [GADimension.SignalID]: cookies.get(CookieNames.SIGNAL_ID),
        [GADimension.WebIDSession]: cookies.get(CookieNames.WEB_ID),
        [GADimension.PromoIA]: cookies.get(CookieNames.PROMO_IA),
        [GADimension.MSClickID]: msclkid,
        [GADimension.BrandCode]: brandCode,
        [GADimension.ApplicationType]: 'WSL',
        [GADimension.DataSource]: datasource,
        [GADimension.ApplicationVersion]: window.version,
        [GADimension.VisitId]: visitid,
        [GADimension.BrandName]: brandName,
        [GADimension.SiteName]: siteName,
        [GADimension.HostApplicationType]: ba_hostapptype,
        [GADimension.HostApplicationVersion]: ba_hostappversion,
        [GADimension.FacebookClickId]: cookies.get(CookieNames.FACEBOOK_CLICK_ID),
        [GADimension.FacebookBrowserId]: cookies.get(CookieNames.FACEBOOK_BROWSER_ID),
        [GADimension.MoneyType]: playMode
      });
    }
  }, [
    acid,
    ba_hostapptype,
    ba_hostappversion,
    brandCode,
    brandName,
    datasource,
    initialized,
    locale,
    msclkid,
    playMode,
    siteName,
    trackingId,
    visitid
  ]);

  useEffect(() => {
    if (initialized) {
      ReactGA.set({ [GADimension.PlayerCurrency]: currency });
      if (!isAuthenticated) ReactGA.set({ [GADimension.Amount]: null });
    }
  }, [currency, initialized, isAuthenticated]);

  useEffect(() => {
    if (!firstRender) {
      window.__GTM_DATA_LAYER__?.push({ event: userWebId ? 'login' : 'logout', userId: userWebId });
    }
  }, [firstRender, userWebId]);

  useEffect(() => {
    if (!snowplowEnabled || !analyticsConsented || !hasClientSession) return;

    const tracker = newTracker(
      'starsgroup-prod1',
      process.env.NODE_ENV === 'development'
        ? 'com-starsgroup-prod1.mini.snplow.net'
        : `collector.${cookieDomain}`,
      {
        appId: 'stars-web-client',
        cookieDomain,
        plugins: [TimezonePlugin(), OptimizelyXPlugin()],
        contexts: { webPage: true, session: true }
      }
    );

    if (userWebId) tracker.core.setUserId(userWebId);
    tracker.core.addGlobalContexts([
      () => {
        const state = store.getState();
        const { version } = window;
        const languageCode = locale as App['languageCode'];
        const oddsFormat = oddsFormatMap[cookies.get(CookieNames.ODDS_FORMAT) as OddsFormat];
        const playMode = select.currency.getPlayMode(state);
        const brandId = select.cms.getBrandId(state);
        const siteId = select.cms.getSiteId(state);
        return app({ version, siteId, brandId, languageCode, oddsFormat, playMode });
      },
      () => {
        const state = store.getState();
        const isAuthenticated = select.session.isAuthenticated(state);
        const rmBalance = select.account.getTotalBalance(state);
        if (isAuthenticated && isNumber(rmBalance)) return user({ rmBalance });
      }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, analyticsConsented, hasClientSession, snowplowEnabled]);

  useEffect(() => setUserId(userWebId || null), [userWebId]);

  useEffect(() => {
    if (initialized && analyticsPath) {
      if (
        analyticsPath.startsWith('/modal/') ||
        firstPageView.current ||
        analyticsPath !== prevAnalyticsPath.current
      ) {
        pageView(analyticsPath, pageTitle);
        firstPageView.current = false;
      }
      prevAnalyticsPath.current = analyticsPath;
    }
  }, [analyticsPath, initialized, pageTitle, pageView]);

  return <AnalyticsContext.Provider value={{ initialized }}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsProvider;
